import type { InvoiceLineTaxType } from '@/types';

/** A VAT code as the document forms hold it: select option plus what the totals need. */
export interface TaxOption {
    label: string;
    value: string;
    rate: number;
    name: string;
    /** EN 16931 VAT category, which the totals group on. */
    category: string;
}

/** What a totals block needs of one code's VAT on a document. */
interface TaxTotal {
    percentage: number;
    name: string;
    amount: number;
    category?: string;
}

/** One VAT row of a totals block. */
export interface TaxTotalGroup {
    key: string;
    percentage: number;
    category?: string;
    /** One name per code in the group. */
    names: string[];
    amount: number;
}

// Mirrors VatTreatment::vatCategory() in milo-api, which groups a document's VAT on it.
const VAT_CATEGORY_BY_TREATMENT = new Map([
    ['standard', 'S'],
    ['reduced', 'S'],
    ['zero', 'Z'],
    ['exempt', 'E'],
    ['special_scheme', 'E'],
    ['reverse_charge', 'AE'],
    ['intra_community', 'K'],
    ['export', 'G'],
]);

/** What a picker or a document shows for a VAT code: the API's label, else the plain name. */
export function taxLabel(tax: Pick<InvoiceLineTaxType, 'name' | 'label'>): string {
    return tax.label ?? tax.name;
}

/** The EN 16931 VAT category of a code; one without a treatment goes by its rate, as in the API. */
export function vatCategory(tax: Pick<InvoiceLineTaxType, 'treatment' | 'amount'>): string {
    return VAT_CATEGORY_BY_TREATMENT.get(tax.treatment?.value ?? '') ?? (tax.amount > 0 ? 'S' : 'Z');
}

export function toTaxOption(tax: InvoiceLineTaxType): TaxOption {
    return { label: taxLabel(tax), value: tax.id, rate: tax.amount, name: tax.name, category: vatCategory(tax) };
}

/**
 * The VAT rows of a totals block, grouped the way the API and the PDF do: per VAT category and
 * rate, so two codes of one kind (services and supplies within the EU) share a row. A code
 * without a category keeps a row of its own.
 */
export function groupTaxTotals(items: TaxTotal[]): TaxTotalGroup[] {
    const groups = new Map<string, TaxTotalGroup>();

    items.forEach((item, index) => {
        const key = item.category ? `${item.category}:${item.percentage}` : `#${index}`;
        const group = groups.get(key);

        if (group) {
            group.names.push(item.name);
            group.amount += item.amount;
        } else {
            groups.set(key, {
                key,
                percentage: item.percentage,
                category: item.category,
                names: [item.name],
                amount: item.amount,
            });
        }
    });

    return [...groups.values()];
}

/** Highest rate first, so the code most invoices need leads; the API's order breaks ties. */
export function sortTaxes<T extends { amount: number }>(taxes: T[]): T[] {
    return [...taxes].sort((a, b) => b.amount - a.amount);
}

/** The code a new line starts with when nothing says otherwise: the standard rate, else the first on offer. */
export function pickDefaultTax<T extends Pick<InvoiceLineTaxType, 'treatment'>>(taxes: T[]): T | undefined {
    return taxes.find((tax) => tax.treatment?.value === 'standard') ?? taxes[0];
}

/**
 * The codes on offer plus those a document's own lines carry: a code retired since the
 * document was made is no longer offered, but must stay selected and keep its VAT.
 */
export function withLineTaxes<T extends { id: string }>(offered: T[], lineTaxes: Array<T | null | undefined>): T[] {
    const taxes = [...offered];
    const seen = new Set(offered.map((tax) => tax.id));

    for (const tax of lineTaxes) {
        if (!tax || seen.has(tax.id)) continue;
        seen.add(tax.id);
        taxes.push(tax);
    }

    return taxes;
}
